import { gql } from '@apollo/client'

export const FILE_FRAGMENT = gql`
	fragment fileFragment on File {
		id
		filename
		fileUrl
		blobId
		byteSize
		contentType
		imageUrls(size: ["100x100", "200x200", "500x500"])
	}
`

const WORKER_AVATAR_FRAGMENT = gql`
	fragment avatar on Document {
		id
		file {
			fileUrl
		}
	}
`

const WORK_USER_FRAGMENT = gql`
	fragment user on User {
		changePasswordRequired
	}
`

export const WORKER_FRAGMENT = gql`
	fragment workerFragment on Worker {
		id
		workAuthorizedAt
		lastName
		firstName
		email
		phone
		phoneSecondary
		phoneVerifiedAt
		whatsappPhone
		countryOfBirth
		countryOfOriginalLicense
		profession
		candidateType
		beside
		user {
			...user
		}
		addresses {
			country
		}
		profilePercentageCompleted
		initialScreeningResult
		avatar {
			...avatar
		}
		resumes {
			documentType
			id
			note
			file {
				...fileFragment
			}
		}
		workerDisciplines {
			id
			discipline {
				id
				disciplineName
				disciplineType
			}
		}
		workerDisciplineSpecialties {
			id
			isPrimary
			specialty {
				id
				name
			}
			discipline {
				disciplineName
				disciplineType
			}
		}

		customField1
		customField2
		customField3
		customField4
		customField5
		customField6

		sclProvider {
			provider
			providerId
		}

		staffingReferralUser {
			applyUrl
			acceptUrl
		}

		currentPipelineStageTracking
		currentPipelineStageMapped
		unlawfulStatus
		visitedUs
		canApplyJob
		visaType
		hasConfirmedStartWork
		fromOfferAcceptedJobIds
	}
	${WORKER_AVATAR_FRAGMENT}
	${WORK_USER_FRAGMENT}
	${FILE_FRAGMENT}
`

export const WORKER_ASSIGNMENT_FRAGMENT = gql`
	fragment workerAssignmentFragment on WorkerAssignment {
		acceptedAt
		actualEndTime
		actualStartTime
		availableToExtend
		customFields
		externalId
		id
		isCancelled
		isExtend
		note
		permittedActions
		reasonCancelled
		scheduledEndTime
		scheduledStartTime
		shiftTypes
		status
		payType
		payRate
		salary
	}
`

const WORKER_ADDRESS_LOCATION_IMAGE = gql`
	fragment locationImage on LocationImage {
		city
		id
		mobileImage {
			fileUrl
		}
		state
		stateName
		thumbnailImage {
			fileUrl
		}
		webImage {
			fileUrl
		}
	}
`

const WORKER_ADDRESS_FRAGMENT = gql`
	fragment workerAddressFragment on Address {
		aptNumber
		city
		country
		default
		externalId
		id
		latitude
		locationImage {
			...locationImage
		}
		longitude
		name
		note
		state
		stateName
		street
		zipcode
	}
	${WORKER_ADDRESS_LOCATION_IMAGE}
`

export const FULL_WORKER_FRAGMENT = gql`
	fragment fullWorkerFragment on Worker {
		activeSubmittedJobsCount
		addresses {
			...workerAddressFragment
		}
		agreedToTerms
		atsSource
		attExpirationDate
		attorney
		availableStartDate
		avatar {
			fileUrl
			thumbnails
		}
		backToBedsideNursing
		beside
		candidateType
		compactLicense
		countryOfBirth
		countryOfOriginalLicense
		createdAt
		currentOnboardingSteps {
			name
			status
			dueDate
		}
		currentPipelineStage
		customField1
		customField2
		customField3
		customField4
		customField5
		customField6
		dateOfOriginalLicense
		educationLevel
		email
		employmentPetitionFiled
		enableEmail
		enableNotification
		enableSms
		engagementScore
		expectedOptDate
		externalCreatedAt
		externalFriendlyId
		externalSub
		firstName
		fullName
		hasAbrOrArrtCertification
		hasAmtOtAscpCertification
		hasAtt
		hasBscMedicalLab
		hasCrt
		hasEducatedInUs
		hasForeignNurseLicense
		hasFpgc
		hasNavleExam
		hasNbcot
		hasNpte
		hasPlanOpt
		hasPtal
		hasStartedVisaScreening
		id
		initialScreeningResult
		internalSub
		isActiveStatus
		jobApplicantsCount
		lastName
		licenseStates
		maidenName
		matchFeedbackAction
		matchingPercentage
		nclexState
		nclexTestDate
		needsSponsorship
		noTalentMatches
		otherNurseLicenseCountries
		otherPreferredWorkTypes
		phone
		phoneSecondary
		phoneVerifiedAt
		preferredContract
		preferredShifts
		preferredWorkTypes
		priorityDate
		profession
		profilePercentageCompleted
		recentJobTitle
		referralCode
		referredCode
		region
		scheduledExam
		schoolName
		secondaryRegion
		source
		status
		submissionReadyStatus
		tenantId
		totalExperienceInMonths
		unlawfulStatus
		userNote
		verifiedPreferredLocations
		visaExpirationDate
		visaType
		visaTypeVisited
		visitedUs
		whatsappPhone
		workAuthorizedAt
		workInHealthcare
		yearInCurrentUnit
		yearsOfTotalExperience
		zipcode
		workerSpecialties {
			category
			completedChecklist
			id
			status
			specialtyLongName
			isPrimary
		}
		resumes {
			id
			filename
			fileUrl
			thumbnails
			contentType
			blobId
			imageUrls(size: ["100x100", "200x200", "500x500"])
		}
		company {
			companyConfig {
				adpUrl
				companyId
				oneSignalAppId
			}
		}
	}
	${WORKER_ADDRESS_FRAGMENT}
`
